/**
 * Valuation Engine Types
 *
 * Types for the compute-valuation edge function input/output.
 * Used by the valuation wizard screen and result views.
 */

// ============================================================================
// Input Types
// ============================================================================

/** Stage of the startup */
export type ValuationStage = 'pre_seed' | 'seed' | 'series_a' | 'series_b' | 'growth';

/** Business model classification */
export type BusinessModel = 'saas' | 'marketplace' | 'hardware' | 'services' | 'other';

/** Market size classification */
export type MarketSize = 'small' | 'medium' | 'large' | 'massive';

/** Competitive intensity classification */
export type CompetitiveIntensity = 'low' | 'medium' | 'high';

/** Input payload for compute-valuation edge function */
export interface ValuationInput {
  sector: string;
  stage: ValuationStage;
  region: string;
  teamSize: number;
  monthlyRevenue: number;
  revenueGrowthRate?: number;
  businessModel: BusinessModel;
  marketSize: MarketSize;
  competitiveIntensity: CompetitiveIntensity;
  hasPrototype: boolean;
  hasRevenue: boolean;
  hasStrategicPartnerships: boolean;
  exitTimeline?: number;
  targetRaise?: number;
  expectedExitValue?: number;
  // Optional detailed inputs for specific methods
  berkusFactors?: Record<string, number>;
  scorecardComparisons?: Record<string, number>;
  riskFactorRatings?: Record<string, number>;
}

// ============================================================================
// Output Types
// ============================================================================

/** Individual method result */
export interface ValuationMethodResult {
  value: number;
  confidence: number;
  breakdown: Record<string, unknown>;
  applicable: boolean;
  currency: string;
}

/** Result from compute-valuation edge function */
export interface ValuationResult {
  id: string;
  startup_id: string;
  config_version: string;
  methodology_version: string;
  input_hash: string;
  wizard_inputs: ValuationInput;
  methods: Record<string, ValuationMethodResult>;
  suggested_range: {
    min: number;
    max: number;
    median: number;
    iqr_low: number;
    iqr_high: number;
    currency: string;
  };
  applicable_methods: number;
  created_at: string;
  cached?: boolean;
  persisted?: boolean;
}

// ============================================================================
// Chart Types (for FootballFieldChart)
// ============================================================================

/** A single valuation method bar for the football field chart */
export interface ValuationMethodBar {
  method: string;
  label: string;
  value: number;
  confidence: number;
  color: string;
}

// ============================================================================
// Step Definitions
// ============================================================================

/** Wizard step identifiers */
export type WizardStep = 0 | 1 | 2 | 3 | 4;

/** Step metadata for the progress indicator */
export interface WizardStepInfo {
  id: WizardStep;
  title: string;
  subtitle: string;
}

export const WIZARD_STEPS: WizardStepInfo[] = [
  { id: 0, title: 'Company Basics', subtitle: 'Industry, stage & team' },
  { id: 1, title: 'Financial Info', subtitle: 'Revenue & business model' },
  { id: 2, title: 'Market & Competition', subtitle: 'Market size & positioning' },
  { id: 3, title: 'Growth Plans', subtitle: 'Exit timeline & raise target' },
  { id: 4, title: 'Results', subtitle: 'Your valuation estimate' },
];

// ============================================================================
// Constants
// ============================================================================

export const SECTORS = [
  'SaaS / Software',
  'Fintech',
  'Healthcare / MedTech',
  'E-commerce',
  'AI / ML',
  'CleanTech / Energy',
  'Consumer',
  'Enterprise',
  'Marketplace',
  'Hardware / DeepTech',
  'BioTech',
  'Other',
] as const;

export const REGIONS = [
  'Zurich',
  'Geneva',
  'Basel',
  'Bern',
  'Lausanne',
  'Lugano',
  'Other Switzerland',
  'DACH Region',
  'Western Europe',
  'Eastern Europe',
  'North America',
  'Asia Pacific',
  'Other',
] as const;

export const STAGE_OPTIONS: { value: ValuationStage; label: string }[] = [
  { value: 'pre_seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series_a', label: 'Series A' },
  { value: 'series_b', label: 'Series B' },
  { value: 'growth', label: 'Growth' },
];

export const BUSINESS_MODEL_OPTIONS: { value: BusinessModel; label: string }[] = [
  { value: 'saas', label: 'SaaS' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'services', label: 'Services' },
  { value: 'other', label: 'Other' },
];

export const MARKET_SIZE_OPTIONS: { value: MarketSize; label: string }[] = [
  { value: 'small', label: 'Small (<$100M)' },
  { value: 'medium', label: 'Medium ($100M-$1B)' },
  { value: 'large', label: 'Large ($1B-$10B)' },
  { value: 'massive', label: 'Massive (>$10B)' },
];

export const COMPETITIVE_INTENSITY_OPTIONS: { value: CompetitiveIntensity; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];
